'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import {
  EDITOR_SESSIONS_CHANGED,
  requestEditorSessions,
  type EditorSession,
} from './editor-sessions';

export function useEditorSessions() {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const userId = user?.uid;
  const projectId = currentProject?.id;
  const key = JSON.stringify([userId, projectId]);
  const [state, setState] = useState<{
    key: string;
    sessions: EditorSession[];
    loading: boolean;
    error: string | null;
  }>({ key: '', sessions: [], loading: true, error: null });
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!userId || !projectId) return;
    let cancelled = false;
    let generation = 0;
    const load = () => {
      const requestGeneration = ++generation;
      setState(previous => ({
        key,
        sessions: previous.key === key ? previous.sessions : [],
        loading: true,
        error: null,
      }));
      void requestEditorSessions(userId, projectId)
        .then(sessions => {
          if (!cancelled && requestGeneration === generation)
            setState({ key, sessions, loading: false, error: null });
        })
        .catch(error => {
          if (!cancelled && requestGeneration === generation)
            setState(previous => ({ ...previous, key, loading: false, error: error.message }));
        });
    };
    load();
    window.addEventListener(EDITOR_SESSIONS_CHANGED, load);
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      window.removeEventListener(EDITOR_SESSIONS_CHANGED, load);
      window.removeEventListener('focus', load);
    };
  }, [userId, projectId, key, revision]);
  return {
    sessions: state.key === key ? state.sessions : [],
    loading: state.key !== key || state.loading,
    error: state.key === key ? state.error : null,
    retry,
  };
}
